import { Autocomplete, Box, Grid, Link, Stack, TextField, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { countries } from '../../../../_mock';
import { RHFCheckbox, RHFSelect, RHFTextField } from '../../../../components/hook-form';
import { PATH_PAGE } from '../../../../routes/paths';
import NoAmountModal from '../NoAmountModal';
import { DonationAPI } from '../../../../services/donation.services';
import { DONATION_TYPE } from '../../../../config';

const LINK_COLOR = '#0275B5';

SimplifiedVolunteerForm.propTypes = {
  projects: PropTypes.array.isRequired,
  currency: PropTypes.string,
  onSuccess: PropTypes.func,
  actionRef: PropTypes.any,
  resolverRef: PropTypes.any,
  methods: PropTypes.any,
  setIsLoading: PropTypes.func,
  isDiscretionary: PropTypes.bool,
};

export default function SimplifiedVolunteerForm({
  onSuccess,
  actionRef,
  resolverRef,
  methods,
  setIsLoading,
  isDiscretionary,
}) {
  const [noAmountOpen, setNoAmountOpen] = useState(false);

  const resolver = yupResolver(
    Yup.object().shape({
      firstName: Yup.string().required('Required'),
      lastName: Yup.string().required('Required'),
      country: Yup.string().required('Required'),
      phone: Yup.string().required('Required'),
      email: Yup.string().required('Required'),
      address: Yup.string().required('Required'),
      website: Yup.string().required('Required'),
    })
  );

  const defaultValues = {
    country: 'PH',
  };

  const {
    reset,
    setValue,
    watch,
    formState: { errors },
  } = methods;

  const values = watch();

  const onSubmitForm = (data, projects) => {
    const payload = createPayload({
      discretionary: isDiscretionary,
      formData: data,
      projects,
    });
    setIsLoading(true);
    DonationAPI.submitDonationInHouse(DONATION_TYPE.IN_HOUSE.VOLUNTEER_SERVICE, payload)
      .then(() => {
        reset(defaultValues);
        setIsLoading(false);
        if (onSuccess) onSuccess();
      })
      .catch((err) => {
        setIsLoading(false);
        alert(`Encountered an error. Please try again.${err}`);
      });
  };

  useEffect(() => {
    resolverRef.current = resolver;
    reset(defaultValues);
    actionRef.current = onSubmitForm;
  }, []);

  return (
    <Box>
      <Typography variant="body1">
        Please fill up the Volunteer Form below then we will connect with you to discuss volunteer opportunities for
        this project.
      </Typography>

      <Grid container spacing={2} mt={2}>
        <Grid item xs={12} md={6}>
          <RHFTextField fullWidth label="First Name" name="firstName" />
        </Grid>
        <Grid item xs={12} md={6}>
          <RHFTextField fullWidth label="Last Name" name="lastName" />
        </Grid>
        <Grid item spacing={1} xs={12} md={6}>
          <RHFTextField fullWidth label="Email" name="email" />
        </Grid>
        <Grid item xs={12} md={6}>
          <RHFTextField name="phone" label="Contact Number*" />
        </Grid>
        <Grid item xs={12} md={8}>
          <RHFTextField fullWidth label="Address" name="address" />
        </Grid>
        <Grid item xs={12} md={4}>
          <Autocomplete
            value={countries.find((c) => c.code === values.country ?? '') ?? ''}
            fullWidth
            onChange={(ev, e) => setValue('country', e.code)}
            renderInput={(e) => <TextField label="Country" {...e} />}
            options={countries}
            getOptionLabel={(e) => e?.label ?? ''}
            isOptionEqualToValue={(a, b) => a.code === b.code}
          />
          <Typography variant="caption" color="red">
            {errors.emailUpdate?.message}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <RHFTextField name="website" label="Website/Social Media URL" />
        </Grid>
      </Grid>

      <Grid container alignItems={'center'} mt={2}>
        <Grid item xs={2} md={0.5}>
          <RHFCheckbox name="emailUpdate" label="" />
        </Grid>
        <Grid xs={10} item>
          <Typography variant="body1" sx={{ mr: 2 }}>
            I am interested to receive CharityPhilippines.org's email updates
          </Typography>
          <Typography variant="caption" color="red">
            {errors.emailUpdate?.message}
          </Typography>
        </Grid>
      </Grid>

      <Stack alignItems={'start'} spacing={2}>
        <Typography variant="body1" sx={{ mr: 2 }}>
          Where did you hear about CharityPhilippines.org?
        </Typography>
        <Box xs={{ mr: 2, width: 220 }}>
          <RHFSelect name="charitySource">
            <option value="">Please Choose</option>
            <option value="Family or Friend">Family or Friend</option>
            <option value="Proactively Looking">Proactively Looking</option>
            <option value="Workplace">Workplace</option>
            <option value="Social Media">Social Media</option>
            <option value="Social Media Advertising">Social Media Advertising</option>
            <option value="Others">Others</option>
          </RHFSelect>
        </Box>
      </Stack>

      <Typography mt={4} sx={{ fontSize: 14, color: '#aeaeae', fontStyle: 'italic' }}>
        If you have questions and clarifications in partnering with CPO, please send us a message
        <Link to={PATH_PAGE.contact} component={RouterLink} color={LINK_COLOR} mx="5px">
          here
        </Link>
        or email us at contact@charityphilippines.org.
      </Typography>

      <NoAmountModal open={noAmountOpen} onClose={() => setNoAmountOpen(false)} />
    </Box>
  );
}

const createPayload = ({ discretionary, formData, projects, causes }) => ({
  firstName: formData.firstName,
  lastName: formData.lastName,
  email: formData.email,
  contactNumber: formData.phone,
  address: formData.address ? formData.address : ' ',
  country: formData.country,
  website: formData.website,
  emailUpdate: formData.emailUpdate,
  charitySource: formData.charitySource === 'Others' ? formData.charityOtherSource : formData.charitySource,
  projects: projects.map((e) => ({ ...e, charityName: e.charityDetails.name, id: e._id })),
  discretion: {
    isDiscretion: discretionary,
    causes,
  },
});
